import React, { PureComponent } from 'react';
import { ReferencedProductType, ReferenceBasketVersion } from '~/Api/RoPriceReferencing/MaxPrice';
import { RoMaxPrice } from '~/Api/User/ProductGroup/RoMaxPrice';
import { parseNumericHumanDate } from '~/DateTime';
import { DebounceInput } from '~/Debounce';
import Checkbox from '~/UI/Checkbox/Checkbox';
import DatePickerInput from '~/UI/DatePickerInput/DatePickerInput';
import Icon from '~/UI/Icon/Icon';
import defaultInputStyles from '~/UI/Input/compact.less';
import Input from '~/UI/Input/Input';
import SaveActivityIndicator, { Status } from '~/UI/SaveActivityIndicator/SaveActivityIndicator';
import Select from '~/UI/Select/Select';

import styles from './styles.less';
import { TailwindTooltip } from '~/libs/ui/atoms/TailwindTooltip/TailwindTooltip';

export interface Value {
	isValid: boolean | null;
	value: string;
}

export interface Values {
	basketVersion: string;
	decisionNumber: Value;
	packageSize: Value;
	productType: string;
	historicProductDataViewDate: {
		value: string;
		dateValue: Date | null;
		isValid: boolean | null;
	};
	fxUpdatePrice: Value;
	fxUpdatePriceEnabled: boolean;
	prgPrbPrice: Value;
	prgPrbPriceEnabled: boolean;
	priPrice: Value;
	priPriceEnabled: boolean;
	lossOfExclusivity: string;
	gtin: string;
}

interface Props {
	productTypes: ReferencedProductType[] | null;
	updateStatus: Status | null;
	roMaxPrice: RoMaxPrice | null;
	isAdditionalPriceRoofAllowed: boolean;
	values: Values;
	detectedDecisionNumber: string;
	versions: ReferenceBasketVersion[] | null;
	onChange(values: Values): void;
	onSave(): void;
}

export default class ParametersPanel extends PureComponent<Props> {
	private renderDecisionNumber() {
		const { values, detectedDecisionNumber, onChange, onSave } = this.props;

		return (
			<label className={styles.parameterLabel} htmlFor='reference-decision-number'>
				<span className={styles.parameterLabelText}>APP/CE Decision Number:</span>
				<div className={styles.decisionNumberInput}>
					<DebounceInput
						value={values.decisionNumber.value}
						onBlur={onSave}
						onChange={(value) => {
							onChange({ ...values, decisionNumber: { value, isValid: null } });
						}}
					>
						<Input
							id='reference-decision-number'
							isInvalid={values.decisionNumber.isValid === false}
							placeholder={detectedDecisionNumber}
							styles={defaultInputStyles}
						/>
					</DebounceInput>
				</div>
			</label>
		);
	}

	private renderReferencePackageSize() {
		const { values, onChange, onSave } = this.props;

		return (
			<label className={styles.parameterLabel} htmlFor='reference-package-size'>
				<span className={styles.parameterLabelText}>Package size:</span>
				<div className={styles.parameterInput}>
					<DebounceInput
						value={values.packageSize.value}
						onBlur={onSave}
						onChange={(value) => {
							onChange({ ...values, packageSize: { value, isValid: null } });
						}}
					>
						<Input
							id='reference-package-size'
							isInvalid={values.packageSize.isValid === false}
							styles={defaultInputStyles}
						/>
					</DebounceInput>
				</div>
			</label>
		);
	}

	private renderGtin() {
		const { roMaxPrice, values, onChange, onSave } = this.props;

		return (
			<label className={styles.parameterLabel} htmlFor='gtin'>
				<span className={styles.parameterLabelText}>GTIN:</span>
				<div className={styles.gtinInput}>
					<DebounceInput
						value={values.gtin}
						onBlur={onSave}
						onChange={(value) => {
							onChange({ ...values, gtin: value });
						}}
					>
						<Input
							id='gtin'
							placeholder={String(roMaxPrice?.finalCalculation.gtin || '')}
							styles={defaultInputStyles}
						/>
					</DebounceInput>
				</div>
			</label>
		);
	}

	private renderVersionSelect() {
		const { versions, values, onChange } = this.props;
		const currentVersion = versions?.find((version) => version.id === values.basketVersion) ?? null;

		const descriptionIcon = (
			<span className={styles.parameterIcon}>
				<Icon name='violet-question-mark' />
			</span>
		);

		return (
			<label className={styles.parameterLabel} htmlFor='reference-basket-version'>
				<span className={styles.parameterLabelText}>Reference basket valid:</span>
				<div className={styles.parameterSelect}>
					<Select
						disabled={versions === null}
						id='reference-basket-version'
						styles={{ select: styles.select }}
						value={values.basketVersion}
						onChange={(event) => {
							onChange({ ...values, basketVersion: event.target.value });
						}}
					>
						{versions !== null
							? versions
									.sort((a, b) => a.order - b.order)
									.map((version) => (
										<option key={version.id} value={version.id}>
											{version.name}
										</option>
									))
							: null}
					</Select>

					{currentVersion?.description && (
						<TailwindTooltip
							reference={<Icon name='violet-question-mark' />}
							displayClass='inline-block ml-2 h-4 align-middle'
							position='top'
						>
							{currentVersion.description}
						</TailwindTooltip>
					)}
				</div>
			</label>
		);
	}

	private renderHistoricViewDateInput() {
		const { values, onChange } = this.props;
		const yearAgo = new Date();
		yearAgo.setDate(yearAgo.getDate() - 365 * 10);

		return (
			<label className={styles.parameterLabel} htmlFor='reference-historic-date'>
				<span className={styles.parameterLabelText}>Reference date:</span>
				<div className={styles.dateParameterInput}>
					<DatePickerInput
						clearable
						dateValue={values.historicProductDataViewDate.dateValue}
						disabledDays={{ after: new Date(), before: yearAgo }}
						isInvalid={values.historicProductDataViewDate.isValid === false}
						placeholder='Today'
						styles={defaultInputStyles}
						value={values.historicProductDataViewDate.value}
						onChange={(value) => {
							onChange({
								...values,
								historicProductDataViewDate: {
									value,
									dateValue: parseNumericHumanDate(value),
									isValid: null,
								},
							});
						}}
					/>
				</div>
			</label>
		);
	}

	private renderProductTypeSelect() {
		const { productTypes, values, onChange } = this.props;
		const usedReferencedProductType = this.resolveUsedReferencedProductType();

		return (
			<label className={styles.parameterLabel} htmlFor='reference-product-type'>
				<span className={styles.parameterLabelText}>Medicine Category:</span>
				<div className={styles.parameterSelect}>
					<Select
						disabled={!productTypes}
						id='reference-product-type'
						styles={{ select: styles.productTypeSelect }}
						value={values.productType}
						onChange={(event) => {
							onChange({ ...values, productType: event.target.value });
						}}
					>
						{productTypes
							? productTypes.map((productType) => (
									<option key={productType.id} value={productType.id ?? ''}>
										{productType.title}
										{productType.id === null && usedReferencedProductType !== null
											? ` (${usedReferencedProductType.title})`
											: null}
									</option>
							  ))
							: null}
					</Select>
				</div>
			</label>
		);
	}

	private renderFxPriceUpdate() {
		const { roMaxPrice, values, onChange, onSave } = this.props;

		return (
			<label className={styles.parameterLabel} htmlFor='fx-price-update'>
				<span className={styles.parameterLabelText}>
					<Checkbox
						checked={values.fxUpdatePriceEnabled}
						onChange={(value) => {
							onChange({ ...values, fxUpdatePriceEnabled: value });
						}}
					/>
					FX price update:
				</span>
				<div className={styles.refPriceInput}>
					<DebounceInput
						value={values.fxUpdatePrice.value}
						onBlur={onSave}
						onChange={(value) => {
							onChange({ ...values, fxUpdatePrice: { value, isValid: null } });
						}}
					>
						<Input
							id='fx-price-update'
							isInvalid={values.fxUpdatePrice.isValid === false}
							placeholder={roMaxPrice?.finalCalculation.fxUpdatePrice?.value}
							styles={defaultInputStyles}
						/>
					</DebounceInput>
				</div>
			</label>
		);
	}

	private renderPrgPrbPrice() {
		const { roMaxPrice, values, onChange, onSave } = this.props;

		return (
			<label className={styles.parameterLabel} htmlFor='prb-prg-price'>
				<span className={styles.parameterLabelText}>
					<Checkbox
						checked={values.prgPrbPriceEnabled}
						onChange={(value) => {
							onChange({ ...values, prgPrbPriceEnabled: value });
						}}
					/>
					PRG / PRB price:
				</span>
				<div className={styles.refPriceInput}>
					<DebounceInput
						value={values.prgPrbPrice.value}
						onBlur={onSave}
						onChange={(value) => {
							onChange({ ...values, prgPrbPrice: { value, isValid: null } });
						}}
					>
						<Input
							id='prb-prg-price'
							isInvalid={values.prgPrbPrice.isValid === false}
							placeholder={roMaxPrice?.finalCalculation.prgPrbPrice?.value}
							styles={defaultInputStyles}
						/>
					</DebounceInput>
				</div>
			</label>
		);
	}

	private renderPriPrice() {
		const { roMaxPrice, values, onChange, onSave } = this.props;

		return (
			<label className={styles.parameterLabel} htmlFor='pri-price'>
				<span className={styles.parameterLabelText}>
					<Checkbox
						checked={values.priPriceEnabled}
						onChange={(value) => {
							onChange({ ...values, priPriceEnabled: value });
						}}
					/>
					PRI price:
				</span>
				<div className={styles.refPriceInput}>
					<DebounceInput
						value={values.priPrice.value}
						onBlur={onSave}
						onChange={(value) => {
							onChange({ ...values, priPrice: { value, isValid: null } });
						}}
					>
						<Input
							id='pri-price'
							isInvalid={values.priPrice.isValid === false}
							placeholder={roMaxPrice?.finalCalculation.priPrice?.value}
							styles={defaultInputStyles}
						/>
					</DebounceInput>
				</div>
			</label>
		);
	}

	private renderLossOfExclusivity() {
		const { roMaxPrice, values, onChange, onSave } = this.props;

		return (
			<label className={styles.parameterLabel} htmlFor='loss-of-exclusivity'>
				<span className={styles.parameterLabelText}>Loss of exclusivity:</span>
				<div className={styles.lossOfExclusivityInput}>
					<DebounceInput
						value={values.lossOfExclusivity}
						onBlur={onSave}
						onChange={(value) => {
							onChange({ ...values, lossOfExclusivity: value });
						}}
					>
						<Input
							id='loss-of-exclusivity'
							placeholder={String(roMaxPrice?.finalCalculation.lossOfExclusivity || '')}
							styles={defaultInputStyles}
						/>
					</DebounceInput>
				</div>
			</label>
		);
	}

	private resolveUsedReferencedProductType() {
		const { productTypes, roMaxPrice } = this.props;

		if (
			productTypes === null ||
			roMaxPrice === null ||
			roMaxPrice.reference.referencedProductType !== null ||
			roMaxPrice.finalCalculation.referencedProductType === null
		) {
			return null;
		}
		return (
			productTypes.find((productType) => productType.id === roMaxPrice.finalCalculation.referencedProductType) ?? null
		);
	}

	public render() {
		const { isAdditionalPriceRoofAllowed, updateStatus } = this.props;

		return (
			<div className={styles.container} data-testid='parameters-panel'>
				<div className={styles.content}>
					<div className={styles.parameters}>
						<p className={styles.parametersDescription}>Maximum price calculation parameters:</p>

						<div>
							{this.renderHistoricViewDateInput()}
							{this.renderVersionSelect()}
							{this.renderProductTypeSelect()}
						</div>

						<div>
							{this.renderDecisionNumber()}
							{this.renderReferencePackageSize()}
							{this.renderGtin()}
							{isAdditionalPriceRoofAllowed ? null : (
								<SaveActivityIndicator
									hideAfterMilliseconds={3000}
									status={updateStatus}
									testId='parameters-panel-save-indicator'
								/>
							)}
						</div>

						<div>
							{isAdditionalPriceRoofAllowed ? this.renderFxPriceUpdate() : null}
							{isAdditionalPriceRoofAllowed ? this.renderPrgPrbPrice() : null}
							{isAdditionalPriceRoofAllowed ? this.renderPriPrice() : null}
						</div>

						<div>
							{isAdditionalPriceRoofAllowed ? this.renderLossOfExclusivity() : null}
							{isAdditionalPriceRoofAllowed ? (
								<SaveActivityIndicator
									hideAfterMilliseconds={3000}
									status={updateStatus}
									testId='parameters-panel-save-indicator'
								/>
							) : null}
						</div>
					</div>
				</div>
			</div>
		);
	}
}
